// i18n for the marketing site.
//
// The English text is the source of truth. Every other language is a dictionary keyed by the exact
// English string (src/i18n/dict/{hi,ar,es}.ts). That way a translation can never go stale silently:
// if someone edits the English, its old translation no longer matches and the string shows up as
// missing and the prerender (scripts/prerender.mjs) fails the build instead of shipping outdated copy.
//
//   t('hero.title', { defaultValue: 'Coach more clients' })  // keyed English from locales/translations.ts
//   tr('Coach more clients')                                  // inline English string
//
// Both resolve the English first, then look that English up in the visitor's language.
import { LanguageCode } from '../types/language';
import { translations as embeddedTranslations } from '../locales/translations';
import { DICTS as SITE_DICTS } from '../i18n/dict';

const english: Record<string, any> = embeddedTranslations.en;

const DICTS: Partial<Record<LanguageCode, Record<string, string>>> = SITE_DICTS;

// Strings looked up but not translated, per language (the prerender fails a /hi, /ar or /es page that has any;
// visible in dev tools as window.__i18nMissing).
const missing: Record<string, Set<string>> = {};
if (typeof window !== 'undefined') (window as any).__i18nMissing = missing;

// Values already in the target language (a translated array passed through tr() again).
const TRANSLATED: Partial<Record<LanguageCode, Set<string>>> = Object.fromEntries(
  Object.entries(DICTS).map(([lang, dict]) => [lang, new Set(Object.values(dict ?? {}))]),
);

function lookup(text: string, lang: LanguageCode): string {
  if (lang === 'en' || !text || !/[A-Za-z]/.test(text)) return text;
  const hit = DICTS[lang]?.[text];
  if (hit !== undefined) return hit;
  if (TRANSLATED[lang]?.has(text)) return text;
  (missing[lang] ??= new Set()).add(text);
  return text;
}

function translateDeep(value: any, lang: LanguageCode): any {
  if (typeof value === 'string') return lookup(value, lang);
  if (Array.isArray(value)) return value.map((v) => translateDeep(v, lang));
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, translateDeep(v, lang)]));
  }
  return value;
}

function interpolate(text: string, params?: Record<string, any>): string {
  if (!params) return text;
  return text.replace(/\{\{(\w+)\}\}/g, (match: string, k: string) => {
    if (k === 'defaultValue' || k === 'returnObjects') return match;
    return params[k] !== undefined ? String(params[k]) : match;
  });
}

function getNested(obj: any, path: string[]) {
  let cur = obj;
  for (const k of path) {
    if (cur === undefined || cur === null) return undefined;
    cur = cur[k];
  }
  return cur;
}

/** Keyed lookup: English from locales/translations.ts (or defaultValue), then translated. */
export function t(key: string, lang: LanguageCode = 'en', params?: Record<string, any>): any {
  let value: any = getNested(english, key.split('.'));
  if (value === undefined && params && params.defaultValue !== undefined) value = params.defaultValue;
  if (value === undefined) {
    if ((import.meta as any).env?.DEV) console.warn(`⚠️ Translation key missing: ${key}`);
    return key;
  }
  if (typeof value === 'object') return translateDeep(value, lang);
  if (typeof value !== 'string') return value;
  return interpolate(lookup(value, lang), params);
}

/** Inline English string → visitor's language. Use {{name}} placeholders for values. */
export function tr(text: string, lang: LanguageCode = 'en', params?: Record<string, any>): string {
  return interpolate(lookup(text, lang), params);
}

// Format date according to language
export function formatDate(date: Date, lang: LanguageCode): string {
  const locales: Record<LanguageCode, string> = { en: 'en-US', hi: 'hi-IN', es: 'es-ES', ar: 'ar-AE' };
  try {
    return new Intl.DateTimeFormat(locales[lang], { year: 'numeric', month: 'long', day: 'numeric' }).format(date);
  } catch {
    return date.toDateString();
  }
}
